import { Material, Mesh, Object3D, Texture } from 'three';

/**
 * Release every GPU resource an Object3D subtree owns: geometries, materials, and the textures those
 * materials reference.
 *
 * Three.js never does this on garbage collection — an undisposed BufferGeometry or Material holds its
 * GPU buffers for the lifetime of the page. Dropping the JS reference alone is not enough.
 *
 * PASS ONE `seen` SET ACROSS EVERY SCENE THAT MIGHT SHARE INSTANCES. A parsed GLTF and the
 * `convertGltf()` output derived from it are related by `SkeletonUtils.clone()`, which copies the object
 * hierarchy but REUSES geometries and materials — so walking both with separate sets would dispose the
 * same instances twice. Double disposal is not fatal but produces WebGL warning noise and hides real
 * problems.
 *
 * Only ever call this for something nothing is rendering any more; a disposed geometry still referenced
 * by a live mesh renders as blank or black with no error.
 */
export function disposeObject3D(root: Object3D, seen: WeakSet<object> = new WeakSet()): void {
    root.traverse((node) => {
        const mesh = node as Mesh;

        if (mesh.geometry && !seen.has(mesh.geometry)) {
            seen.add(mesh.geometry);
            mesh.geometry.dispose();
        }

        const materials: Material[] = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
        for (const material of materials) {
            if (seen.has(material)) continue;
            seen.add(material);
            // Any Texture-valued property is a map (map, normalMap, aoMap, …). Enumerating beats naming
            // them: the set differs per material type and grows between three.js versions.
            for (const value of Object.values(material as unknown as Record<string, unknown>)) {
                const texture = value as Texture | null;
                if (texture && (texture as Texture).isTexture && !seen.has(texture)) {
                    seen.add(texture);
                    texture.dispose();
                }
            }
            material.dispose();
        }
    });
}
